/**
 * ч.1374 — таблична частина друкованих форм продажу й купівлі за зразком 1С (видаткова
 * накладна, акт, рахунок/замовлення постачальнику):
 *   № | Товари (роботи, послуги) | Кіл-сть | Од. | [Знижка, %] | Ціна … | Сума …
 * Заголовки й підсумки — за режимом ПДВ документа («Враховувати ПДВ» / «Сума включає ПДВ»):
 *   без ПДВ у документі: «Ціна», «Сума», «Всього:», прописом + «Без ПДВ»;
 *   ціни без ПДВ:        «Ціна без ПДВ», «Сума без ПДВ», «Всього:», «Сума ПДВ:», «Всього із ПДВ:»;
 *   ціни з ПДВ:          «Ціна з ПДВ», «Сума з ПДВ», «Всього:», «У тому числі ПДВ:».
 * Далі «Всього найменувань N, на суму … грн.», сума прописом жирним, «У т.ч. ПДВ: …» прописом
 * і товста лінія — як у 1С. Рахунок на оплату має власну сітку (`CustomerInvoicePrintPage`).
 */
import { amountToWordsUAFull } from '@/utils/numberToWordsUA';
import type { VatMode } from '@/shared/utils/vatFlags';

export interface PrintLine {
  name: string;
  qty: number;
  uom: string;
  price: number;
  amount: number;
  discount?: number | null;
  /** Додаткові колонки (ключ → значення), напр. склад рядка. */
  extra?: Record<string, string>;
}

export const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

export function vatHeaders(mode: VatMode): { price: string; amount: string } {
  const suffix = mode === 'included' ? ' з ПДВ' : mode === 'excluded' ? ' без ПДВ' : '';
  return { price: `Ціна${suffix}`, amount: `Сума${suffix}` };
}

/** Рядки підсумків за режимом: [підпис, сума]. */
export function vatTotals(mode: VatMode, net: number, vat: number): Array<[string, number]> {
  if (mode === 'none') return [['Всього:', net]];
  if (mode === 'included') return [['Всього:', net + vat], ['У тому числі ПДВ:', vat]];
  return [['Всього:', net], ['Сума ПДВ:', vat], ['Всього із ПДВ:', net + vat]];
}

/** Сума прописом і рядок про ПДВ під нею. */
export function vatWords(mode: VatMode, net: number, vat: number): [string, string] {
  const total = mode === 'none' ? net : net + vat;
  return [amountToWordsUAFull(total),
    mode === 'none' ? 'Без ПДВ' : `У т.ч. ПДВ: ${amountToWordsUAFull(vat)}`];
}

export default function PrintLines1c({ mode, lines, net, vat, nameHeader = 'Товари (роботи, послуги)',
  countLabel = 'Всього найменувань', extraColumns = [] }: {
  mode: VatMode;
  lines: PrintLine[];
  /** Σ без ПДВ і Σ ПДВ за рядками (у режимі «без ПДВ у документі» vat = 0). */
  net: number;
  vat: number;
  nameHeader?: string;
  countLabel?: string;
  extraColumns?: { key: string; header: string; width?: string }[];
}) {
  const h = vatHeaders(mode);
  const hasDiscount = lines.some((l) => l.discount != null && l.discount > 0);
  const total = mode === 'none' ? net : net + vat;
  const [words, vatLine] = vatWords(mode, net, vat);
  return (
    <div className="pl1c">
      <style>{`
        .pl1c table.lines { width: 100%; border-collapse: collapse; margin: 0.4rem 0 0.3rem;
          border: 2px solid #000; font-size: 8pt; font-family: Arial, Helvetica, sans-serif; color: #000; }
        .pl1c table.lines th { background: #eee; font-size: 9pt; font-weight: bold; text-align: center;
          border: 1px solid #000; padding: 3px 2px; text-transform: none; letter-spacing: 0; color: #000; }
        .pl1c table.lines td { border: 1px solid #000; padding: 0 3px; vertical-align: top; }
        .pl1c .r { text-align: right; white-space: nowrap; }
        .pl1c .c { text-align: center; }
        .pl1c table.totals { width: 100%; border-collapse: collapse; font-weight: bold;
          font-family: Arial, Helvetica, sans-serif; font-size: 9pt; color: #000; }
        .pl1c table.totals td { padding: 1px 3px; border: none; }
        .pl1c .count { font-size: 8pt; margin-top: 0.5rem; font-family: Arial, Helvetica, sans-serif; color: #000; }
        .pl1c .words { font-weight: bold; font-size: 9pt; margin: 0.15rem 0 0.4rem; padding-bottom: 0.4rem;
          border-bottom: 2px solid #000; font-family: Arial, Helvetica, sans-serif; color: #000; }
      `}</style>
      <table className="lines">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>№</th>
            <th>{nameHeader}</th>
            {extraColumns.map((c) => <th key={c.key} style={{ width: c.width }}>{c.header}</th>)}
            <th style={{ width: '8%' }}>Кіл-сть</th>
            <th style={{ width: '6%' }}>Од.</th>
            {hasDiscount && <th style={{ width: '8%' }}>Знижка, %</th>}
            <th style={{ width: '11%' }}>{h.price}</th>
            <th style={{ width: '12%' }}>{h.amount}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td>
              <td>{l.name}</td>
              {extraColumns.map((c) => <td key={c.key} className="c">{l.extra?.[c.key] ?? ''}</td>)}
              <td className="r">{fmtQty(l.qty)}</td>
              <td>{l.uom}</td>
              {hasDiscount && (
                <td className="r">
                  {l.discount != null && l.discount > 0
                    ? l.discount.toLocaleString('uk-UA', { maximumFractionDigits: 2 }) : ''}
                </td>
              )}
              <td className="r">{fmtMoney(l.price)}</td>
              <td className="r">{fmtMoney(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="totals">
        <tbody>
          {vatTotals(mode, net, vat).map(([label, v]) => (
            <tr key={label}>
              <td style={{ width: '66%', textAlign: 'right' }}>{label}</td>
              <td />
              <td style={{ width: '14%', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="count">{countLabel} {lines.length}, на суму {fmtMoney(total)} грн.</div>
      <div className="words"><div>{words}</div><div>{vatLine}</div></div>
    </div>
  );
}
